import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import Modal from "@mui/material/Modal";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { createRewardRule, updateRewardRule } from "../../../actions/rewards";
import { modifyCurrencyToSymbol } from "../../../utils/utils";
import ButtonWithLoader from "../../ButtonWithLoader";
import InputController from "../../inputController";
import TextAreaController from "../../inputController/TextAreaController";

const RewardRuleModal = ({
  visible,
  onClose,
  isEdit,
  ruleData,
  programId,
  currency,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 12,
    p: 4,
    overflowY: "auto",
    maxHeight: "90vh",
    "&::-webkit-scrollbar": {
      WebkitAppearance: "none",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "8px",
      border: "1px solid white",
      backgroundColor: "rgba(0,0,0,.5)",
    },
    "&::-webkit-scrollbar:vertical": {
      width: "8px",
    },
  };

  const dispatch = useDispatch();
  const createRewardRuleSuccess = useSelector(
    (state) => state.rewards.createRewardRuleSuccess
  );
  const updateRewardRuleSuccess = useSelector(
    (state) => state.rewards.updateRewardRuleSuccess
  );

  useEffect(() => {
    if (createRewardRuleSuccess === true || createRewardRuleSuccess === false) {
      setIsSubmitting(false);
    }
  }, [createRewardRuleSuccess]);

  useEffect(() => {
    if (updateRewardRuleSuccess === true || updateRewardRuleSuccess === false) {
      setIsSubmitting(false);
    }
  }, [updateRewardRuleSuccess]);

  useEffect(() => {
    if (!visible) {
      setIsSubmitting(false);
    }
  }, [visible]);

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      fixedAmount: "",
      priority: 1,
      displayTitle: "",
      displayMessage: "",
    },
  });

  useEffect(() => {
    if (visible && isEdit && ruleData) {
      const fixedAmount = ruleData.rewardConfig?.fixed_amount_minor
        ? ruleData.rewardConfig.fixed_amount_minor / 100
        : "";

      reset({
        name: ruleData.name,
        fixedAmount: fixedAmount,
        priority: ruleData.priority || 0,
        displayTitle: ruleData.displayTitle || "",
        displayMessage: ruleData.displayMessage || "",
      });
    } else if (visible && !isEdit) {
      reset({
        name: "",
        fixedAmount: "",
        priority: 1,
        displayTitle: "",
        displayMessage: "",
      });
    }
  }, [visible, isEdit, ruleData, reset]);

  const onSubmit = (data) => {
    const rewardConfig = {
      fixed_amount_minor: Math.round(parseFloat(data.fixedAmount) * 100),
    };

    const formData = {
      reward_program_id: programId,
      name: data.name,
      trigger_type: "USER_REGISTERED",
      reward_type: "WALLET_CREDIT",
      reward_config: rewardConfig,
      per_user_limit: 1,
      priority: parseInt(data.priority) || 0,
      display_title: data.displayTitle || null,
      display_message: data.displayMessage || null,
    };

    setIsSubmitting(true);

    if (isEdit && ruleData) {
      const updateData = { ...formData };
      delete updateData.reward_program_id;

      dispatch(
        updateRewardRule(ruleData.id, {
          update: {
            set: updateData,
          },
        })
      );
    } else {
      dispatch(createRewardRule(formData));
    }
  };

  return (
    <Modal
      open={visible}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <FormProvider control={control}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              {isEdit ? "Edit Signup Bonus Rule" : "Add Signup Bonus Rule"}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Higher priority rules are applied first. Only one bonus per user.
            </Alert>

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name="name"
                label="Name"
                required={true}
                styles={{
                  width: "100%",
                }}
                control={control}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name="fixedAmount"
                label={`Bonus Amount${currency ? ` (${modifyCurrencyToSymbol(currency)})` : ""}`}
                type="number"
                required={true}
                styles={{
                  width: "100%",
                }}
                control={control}
                extraRules={{
                  min: {
                    value: 0.01,
                    message: "Amount must be greater than 0",
                  },
                }}
              />
              <Typography
                variant="caption"
                color={currency ? "text.secondary" : "error"}
              >
                {currency
                  ? `Amount credited to user's wallet when they sign up (${currency})`
                  : "Currency not found for this program"}
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name="priority"
                label="Priority"
                type="number"
                required={false}
                styles={{
                  width: "100%",
                }}
                control={control}
              />
              <Typography variant="caption" color="text.secondary">
                Higher number = applied first. Priority 10 beats Priority 5.
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name="displayTitle"
                label="Title"
                required={false}
                styles={{
                  width: "100%",
                }}
                control={control}
              />
              <Typography variant="caption" color="text.secondary">
                Title shown on the bonus screen (e.g., "Welcome Bonus")
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <TextAreaController
                name="displayMessage"
                label="Welcome Message"
                required={false}
                multiline={true}
                styles={{
                  width: "100%",
                }}
                control={control}
              />
              <Typography variant="caption" color="text.secondary">
                Message shown to user when they receive the bonus
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button variant="outlined" color="error" onClick={onClose}>
                Cancel
              </Button>
              {!isEdit && (
                <ButtonWithLoader text="Create" isLoading={isSubmitting} />
              )}
              {isEdit && (
                <ButtonWithLoader text="Update" isLoading={isSubmitting} />
              )}
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default RewardRuleModal;
